import { useState } from 'react';
import {
  AlertTriangle,
  AlertCircle,
  WifiOff,
  PackageCheck,
  RefreshCw,
} from '@/utils/icons';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { InfoTooltip } from '@/components/common/InfoTooltip';
import { cn } from '@/lib/utils';
import { formatTimeAgo } from '@/utils/formatters';
import type { Alert } from '@/types/metrics';

interface AlertsPanelProps {
  alerts: Alert[];
  onAlertClick?: (nodeId: string) => void;
}

interface AlertCategory {
  key: Alert['type'];
  label: string;
  bg: string;
  text: string;
  icon: typeof AlertTriangle;
  severityClass: string;
}

const alertCategories: AlertCategory[] = [
  {
    key: 'offline',
    label: 'Offline',
    bg: 'dls-bg-status-offline',
    text: 'dls-text-white',
    icon: WifiOff,
    severityClass: 'dls-bg-status-offline-quarternary dls-border-status-offline-tertiary dls-text-status-offline hover:dls-bg-status-offline-tertiary',
  },
  {
    key: 'flapping',
    label: 'Flapping',
    bg: 'dls-bg-status-warning',
    text: 'dls-text-white',
    icon: RefreshCw,
    severityClass: 'dls-bg-status-warning-quarternary dls-border-status-warning-tertiary dls-text-status-warning hover:dls-bg-status-warning-tertiary',
  },
  {
    key: 'outdated',
    label: 'Outdated',
    bg: 'dls-bg-status-info',
    text: 'dls-text-white',
    icon: PackageCheck,
    severityClass: 'dls-bg-status-info/10 dls-border-status-info/30 dls-text-status-info hover:dls-bg-status-info/20',
  },
];

export function AlertsPanel({
  alerts,
  onAlertClick,
}: AlertsPanelProps) {
  const [filterType, setFilterType] = useState<Alert['type'] | null>(null);

  // Group alerts by type
  const grouped = alertCategories.map((cat) => ({
    ...cat,
    alerts: alerts.filter((a) => a.type === cat.key),
    count: alerts.filter((a) => a.type === cat.key).length,
  }));

  const activeGroups = grouped.filter((g) => g.count > 0);

  // Show all alerts or filtered by selected type
  const displayedAlerts = filterType ? alerts.filter((a) => a.type === filterType) : alerts;
  const filterGroup = filterType ? grouped.find((g) => g.key === filterType) : null;

  // Get severity class for an individual alert
  const getAlertSeverityClass = (alert: Alert) => {
    const group = alertCategories.find((c) => c.key === alert.type);
    return group?.severityClass ?? 'dls-bg-muted dls-border-border dls-text-foreground';
  };
  const getAlertIcon = (alert: Alert) => {
    const group = alertCategories.find((c) => c.key === alert.type);
    return group?.icon ?? AlertCircle;
  };

  return (
    <Card className="dls-h-full dls-flex dls-flex-col">
      <CardHeader className="dls-shrink-0">
        <CardTitle className="dls-flex dls-items-center dls-gap-1.5">
          <AlertCircle className="dls-h-5 dls-w-5" />
          Alerts
          <InfoTooltip text="Active issues requiring attention. Click an alert type to filter the list below." />
          <span className="dls-ml-auto dls-text-sm dls-font-normal dls-text-muted-foreground">
            {alerts.length} Active
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="dls-flex-1 dls-overflow-y-auto dls-min-h-0">
        {alerts.length === 0 ? (
          <p className="dls-text-sm dls-text-muted-foreground dls-py-4 dls-text-center">No active alerts</p>
        ) : (
          <div className="dls-space-y-3">
            {/* Alert type tiles — single row, equal width */}
            <div className="dls-flex dls-gap-2">
              {activeGroups.map((group) => {
                const isFiltered = filterType === group.key;
                const Icon = group.icon;
                return (
                  <button
                    key={group.key}
                    type="button"
                    onClick={() => setFilterType(isFiltered ? null : group.key)}
                    className={`dls-flex-1 dls-rounded-lg dls-px-2 dls-py-3 dls-text-center dls-transition-all dls-cursor-pointer ${group.bg} ${
                      isFiltered ? 'dls-ring-2 dls-ring-offset-1 dls-ring-current/30' : ''
                    } hover:dls-ring-2 hover:dls-ring-offset-1 hover:dls-ring-current/20 ${
                      filterType && !isFiltered ? 'dls-opacity-50' : ''
                    }`}
                  >
                    <div className="dls-flex dls-items-center dls-justify-center dls-gap-1.5 dls-mb-1">
                      <Icon className={`dls-h-4 dls-w-4 ${group.text} dls-opacity-80`} />
                    </div>
                    <p className={`dls-text-2xl dls-font-bold ${group.text}`}>{group.count}</p>
                    <p className={`dls-text-xs ${group.text} dls-opacity-80`}>{group.label}</p>
                  </button>
                );
              })}
            </div>

            {/* Alert list */}
            {displayedAlerts.length > 0 && (
              <div className="dls-border-t dls-pt-3 dls-mt-1">
                <p className="dls-text-sm dls-font-medium dls-mb-2">
                  {filterGroup ? `${filterGroup.label} Alerts` : 'All Alerts'}
                  <span className="dls-text-muted-foreground dls-font-normal dls-ml-1">
                    ({displayedAlerts.length})
                  </span>
                </p>
                <div className="dls-space-y-2">
                  {displayedAlerts.map((alert, index) => {
                    const Icon = getAlertIcon(alert);
                    return (
                      <div
                        key={`${alert.nodeId}-${alert.type}-${index}`}
                        className={cn(
                          'dls-flex dls-items-start dls-gap-3 dls-p-3 dls-rounded-lg dls-border dls-cursor-pointer dls-transition-colors',
                          getAlertSeverityClass(alert)
                        )}
                        onClick={() => onAlertClick?.(alert.nodeId)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onAlertClick?.(alert.nodeId);
                          }
                        }}
                      >
                        <Icon className="dls-h-4 dls-w-4 dls-mt-0.5 dls-flex-shrink-0" />
                        <div className="dls-flex-1 dls-min-w-0">
                          <span className="dls-font-medium dls-truncate dls-block">{alert.nodeName}</span>
                          <p className="dls-text-sm dls-opacity-90">{alert.message}</p>
                          {alert.since && (
                            <p className="dls-text-xs dls-opacity-75 dls-mt-1">
                              Since {formatTimeAgo(alert.since)}
                            </p>
                          )}
                          {alert.lastContact && (
                            <p className="dls-text-xs dls-opacity-75 dls-mt-1">
                              Last contact {formatTimeAgo(alert.lastContact)}
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
